import {
  CONTEST_STATUS_LABELS,
  CONTRACT_KIND_LABELS,
  formatUah,
  OCCUPANCY_LABELS,
  FUNNEL_LEVEL_LABELS,
  todayKyiv,
  CURRENCY_LABEL,
  type ContestStatus,
  type ContractKind,
  type OccupancyStatus,
} from '@hr/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { FileSpreadsheetIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { z } from 'zod';
import { BarList, ColumnChart, PlanFactList, StatTile } from '@/components/charts';
import { Combobox } from '@/components/combobox';
import { DateInput } from '@/components/date-input';
import { PageHeader } from '@/components/page';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { usePreview } from '@/features/preview/use-preview';
import { useRegistry } from '@/features/registry/use-registry';
import { uk } from '@/i18n/uk';
import { monthLabel } from '@/lib/months';
import { trpc } from '@/lib/trpc';

const TABS = ['overview', 'staff', 'projects', 'services', 'recruitment'] as const;

export const Route = createFileRoute('/_app/analytics')({
  validateSearch: z.object({
    tab: z.enum(TABS).optional().catch(undefined),
    from: z.iso.date().optional().catch(undefined),
    to: z.iso.date().optional().catch(undefined),
    project: z.uuid().optional().catch(undefined),
  }),
  component: AnalyticsPage,
});

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-3 rounded-lg border p-4">
      <h2 className="text-sm font-semibold">{title}</h2>
      {children}
    </section>
  );
}

const pct = (a: number, b: number) => (b ? `${Math.round((a / b) * 100)}%` : '—');

function AnalyticsPage() {
  const t = uk.analytics;
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const registry = useRegistry();
  const openPreview = usePreview();
  const today = todayKyiv();
  // За замовчуванням — з початку року.
  const from = search.from ?? `${today.slice(0, 4)}-01-01`;
  const to = search.to ?? today;
  const setSearch = (patch: Partial<typeof search>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const query = useQuery({
    ...trpc.analytics.overview.queryOptions({ from, to, projectId: search.project }),
    placeholderData: keepPreviousData,
  });
  const d = query.data;
  const fteTotal = d?.staffing.projects.reduce((s, p) => s + p.fte, 0) ?? 0;
  const fteOccupied = d?.staffing.projects.reduce((s, p) => s + p.occupiedFte, 0) ?? 0;
  const accrued = d?.accruedByMonth.reduce((s, m) => s + m.amount, 0) ?? 0;

  return (
    <>
      <PageHeader
        title={t.title}
        actions={
          <Button
            variant="outline"
            disabled={!search.project}
            title={search.project ? undefined : t.donorReportHint}
            onClick={() =>
              openPreview({
                url: `/api/analytics/donor-report.xlsx?projectId=${search.project}&from=${from}&to=${to}`,
              })
            }
          >
            <FileSpreadsheetIcon /> {t.donorReport}
          </Button>
        }
      />
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="text-sm text-muted-foreground">{t.period}</span>
        <DateInput
          className="w-36"
          value={from}
          onChange={(v) => setSearch({ from: v ?? undefined })}
        />
        <span className="text-sm text-muted-foreground">–</span>
        <DateInput
          className="w-36"
          value={to}
          onChange={(v) => setSearch({ to: v ?? undefined })}
        />
        <Combobox
          className="w-48"
          value={search.project}
          onChange={(v) => setSearch({ project: v ?? undefined })}
          options={(registry.data?.projects ?? []).map((p) => ({
            value: p.id,
            label: p.code,
            hint: p.name,
          }))}
          placeholder={uk.registry.project}
          allowClear
        />
      </div>
      {!d ? (
        <Skeleton className="h-96" />
      ) : (
        <Tabs
          value={search.tab ?? 'overview'}
          onValueChange={(v) =>
            setSearch({ tab: v === 'overview' ? undefined : (v as (typeof TABS)[number]) })
          }
        >
          <TabsList className="flex-wrap">
            <TabsTrigger value="overview">{t.tabOverview}</TabsTrigger>
            <TabsTrigger value="staff">{t.tabStaff}</TabsTrigger>
            <TabsTrigger value="projects">{t.tabProjects}</TabsTrigger>
            <TabsTrigger value="services">{t.tabServices}</TabsTrigger>
            <TabsTrigger value="recruitment">{t.tabRecruitment}</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4 pt-2">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <StatTile label={t.headcount} value={d.staffing.headcount} />
              <StatTile
                label={t.fte}
                value={`${Math.round(fteOccupied * 100) / 100} / ${Math.round(fteTotal * 100) / 100}`}
                hint={`${t.vacancies}: ${(d.staffing.occupancy.vacant ?? 0) + (d.staffing.occupancy.partially_occupied ?? 0)}`}
              />
              <StatTile
                label={t.utilization}
                value={d.utilization.rate === null ? '—' : `${d.utilization.rate}%`}
                hint={`${d.utilization.actual} / ${d.utilization.planned} год`}
              />
              <StatTile label={t.accrued} value={formatUah(accrued)} />
              <StatTile
                label={t.reports}
                value={d.services.totals.reports}
                hint={`${d.services.totals.quantity} од. · ${d.services.totals.hours} год`}
              />
              <StatTile label={t.beneficiaries} value={d.services.totals.beneficiaries} />
              <StatTile label={t.hromadas} value={d.services.totals.hromadas} />
              <StatTile
                label={t.contests}
                value={d.recruitment.contests}
                hint={
                  d.recruitment.avgDaysToClose !== null
                    ? `${t.avgDays}: ${d.recruitment.avgDaysToClose}`
                    : undefined
                }
              />
            </div>
            <div className="grid gap-4 lg:grid-cols-2">
              <Card title={t.servicesByMonth}>
                <ColumnChart
                  data={d.services.byMonth.map((m) => ({
                    key: m.key,
                    label: monthLabel(m.key).slice(0, 3),
                    value: m.quantity,
                  }))}
                />
              </Card>
              <Card title={`${t.accruedMonth}, ${CURRENCY_LABEL}`}>
                <ColumnChart
                  data={d.accruedByMonth.map((m) => ({
                    key: m.month,
                    label: monthLabel(m.month).slice(0, 3),
                    value: m.amount,
                  }))}
                  format={formatUah}
                />
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="staff" className="space-y-4 pt-2">
            <div className="grid gap-4 lg:grid-cols-2">
              <Card title={t.byContractKind}>
                <BarList
                  data={Object.entries(d.staffing.byContractKind).map(([k, n]) => ({
                    key: k,
                    label: CONTRACT_KIND_LABELS[k as ContractKind],
                    value: n,
                  }))}
                  empty={t.noData}
                />
              </Card>
              <Card title={t.occupancy}>
                <BarList
                  data={Object.entries(d.staffing.occupancy).map(([k, n]) => ({
                    key: k,
                    label: OCCUPANCY_LABELS[k as OccupancyStatus],
                    value: n,
                  }))}
                  empty={t.noData}
                />
              </Card>
              <Card title={t.byCenter}>
                <PlanFactList
                  planLabel={t.plan}
                  factLabel={t.fact}
                  data={d.staffing.centers.map((c) => ({
                    key: c.name,
                    label: c.name,
                    plan: c.fte,
                    fact: c.occupiedFte,
                  }))}
                />
              </Card>
              <Card title={t.planFact}>
                <PlanFactList
                  planLabel={t.plan}
                  factLabel={t.fact}
                  data={d.utilization.people.map((p) => ({
                    key: p.name,
                    label: p.name,
                    plan: p.planned,
                    fact: p.actual,
                    display: pct(p.actual, p.planned),
                  }))}
                />
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="projects" className="pt-2">
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">{uk.registry.project}</th>
                    <th className="px-3 py-2 text-right font-medium">{uk.registry.staffCount}</th>
                    <th className="px-3 py-2 text-right font-medium">{t.fte}</th>
                    <th className="px-3 py-2 text-right font-medium">{t.budget}</th>
                    <th className="px-3 py-2 text-right font-medium">{t.planned}</th>
                    <th className="px-3 py-2 text-right font-medium">{t.accrued}</th>
                    <th className="px-3 py-2 text-left font-medium">% {t.budget.toLowerCase()}</th>
                  </tr>
                </thead>
                <tbody>
                  {d.budget.map((b) => {
                    const s = d.staffing.projects.find((p) => p.code === b.code);
                    const share = b.budget ? Math.min(100, (b.accrued / b.budget) * 100) : 0;
                    return (
                      <tr key={b.code} className="border-t">
                        <td className="px-3 py-2 font-medium">{b.code}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{s?.positions ?? 0}</td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {s ? `${s.occupiedFte} / ${s.fte}` : '—'}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{formatUah(b.budget)}</td>
                        <td
                          className={
                            b.planned > b.budget && b.budget > 0
                              ? 'px-3 py-2 text-right text-destructive tabular-nums'
                              : 'px-3 py-2 text-right tabular-nums'
                          }
                        >
                          {formatUah(b.planned)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {formatUah(b.accrued)}
                        </td>
                        <td className="min-w-32 px-3 py-2">
                          <div className="flex items-center gap-2" title={`${Math.round(share)}%`}>
                            <span className="h-2 flex-1 rounded-full bg-primary/15">
                              <span
                                className="block h-2 rounded-full bg-primary"
                                style={{ width: `${share}%` }}
                              />
                            </span>
                            <span className="text-xs tabular-nums">{Math.round(share)}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="services" className="space-y-4 pt-2">
            <div className="grid gap-4 lg:grid-cols-2">
              <Card title={t.servicesByType}>
                <BarList
                  data={d.services.byService.map((s) => ({
                    key: s.key,
                    label: s.label,
                    value: s.quantity,
                    tooltip: `${s.label}: ${s.quantity} од., ${s.hours} год, ${s.beneficiaries} отримувачів`,
                  }))}
                  empty={t.noData}
                />
              </Card>
              <Card title={t.byOblast}>
                <BarList
                  data={d.services.byOblast.map((s) => ({
                    key: s.key,
                    label: s.label,
                    value: s.reports,
                    tooltip: `${s.label}: ${s.reports} звітів, ${s.beneficiaries} отримувачів`,
                  }))}
                  empty={t.noData}
                />
              </Card>
            </div>
            <Card title={t.byHromada}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs text-muted-foreground">
                    <tr>
                      <th className="py-1 text-left font-medium">{uk.reports.location}</th>
                      <th className="py-1 text-right font-medium">{t.reports}</th>
                      <th className="py-1 text-right font-medium">{uk.reports.quantity}</th>
                      <th className="py-1 text-right font-medium">{uk.reports.hours}</th>
                      <th className="py-1 text-right font-medium">{t.beneficiaries}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.services.byHromada.map((h) => (
                      <tr key={h.key} className="border-t">
                        <td className="py-1.5">{h.label}</td>
                        <td className="py-1.5 text-right tabular-nums">{h.reports}</td>
                        <td className="py-1.5 text-right tabular-nums">{h.quantity}</td>
                        <td className="py-1.5 text-right tabular-nums">{h.hours}</td>
                        <td className="py-1.5 text-right tabular-nums">{h.beneficiaries}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="recruitment" className="space-y-4 pt-2">
            <div className="grid gap-3 sm:grid-cols-3">
              <StatTile label={t.contests} value={d.recruitment.contests} />
              <StatTile label={t.avgDays} value={d.recruitment.avgDaysToClose ?? '—'} />
              <StatTile
                label={uk.nav.contests}
                value={
                  Object.entries(d.recruitment.byStatus)
                    .map(([s, n]) => `${CONTEST_STATUS_LABELS[s as ContestStatus]}: ${n}`)
                    .join(' · ') || '—'
                }
                className="[&>div:nth-child(2)]:text-base"
              />
            </div>
            <div className="grid gap-4 lg:grid-cols-2">
              <Card title={t.funnel}>
                <BarList
                  max={d.recruitment.funnel[0]?.count}
                  data={d.recruitment.funnel.map((f) => ({
                    key: f.level,
                    label: FUNNEL_LEVEL_LABELS[f.level],
                    value: f.count,
                    display: `${f.count} · ${pct(f.count, d.recruitment.funnel[0]?.count ?? 0)}`,
                  }))}
                  empty={t.noData}
                />
              </Card>
              <Card title={t.sources}>
                <BarList
                  data={d.recruitment.sources.map((s) => ({
                    key: s.source,
                    label: s.source,
                    value: s.applications,
                    display: t.hiredOf(s.hired, s.applications),
                  }))}
                  empty={t.noData}
                />
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      )}
    </>
  );
}
